import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  OnInit,
  output,
  signal,
  TemplateRef,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { SelectOption } from '@fcom/common';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';
import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { stopPropagation } from '@fcom/core/utils';

import { IconButtonSize, IconButtonTheme } from '../../buttons';
import { NotificationTheme } from '../../notifications';
import { PopoverOptions } from '../../popover';
import { CursorType } from '../enums';
import { TextInputComponent } from '../text-input/text-input.component';

@Component({
  selector: 'fcom-combo-box',
  templateUrl: './combo-box.component.html',
  styleUrls: ['./combo-box.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ComboBoxComponent implements OnInit {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  readonly themeService = inject(FinnairThemeService);

  readonly CursorType = CursorType;
  readonly IconButtonSize = IconButtonSize;
  readonly IconButtonTheme = IconButtonTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationTheme = NotificationTheme;
  readonly FinnairTheme = FinnairTheme;

  parentForm = input.required<UntypedFormGroup>();
  controlName = input.required<string>();
  translationPrefix = input<string>();
  options = input.required<SelectOption[]>();
  id = input<string>();
  selectLabelTranslationKey = input('select');
  required = input<boolean>();
  displayMargin = input(true);
  label = input<string>();
  tooltipHeading = input<string>();
  tooltipContent = input<string>();
  tooltipOptions = input<PopoverOptions>();
  disableLabel = input(false);
  disableSelectOption = input(false);

  readonly ctrlField = computed(() => this.parentForm().get(this.controlName()));
  readonly control = computed(() => this.ctrlField());
  readonly idOrControlName = computed(() => this.id() || this.controlName());
  readonly fieldRequired = computed(() =>
    Boolean(this.required() || this.ctrlField()?.hasValidator(Validators.required))
  );
  readonly showAsterisk = computed<boolean>(() => this.required() ?? this.fieldRequired());

  searchable = input(false);
  searchLabel = input<string>();
  clearSearchLabel = input<string>();
  selectLabel = input<string>();
  infoText = input<string>();
  warningText = input<string>();
  optionTemplate = input<TemplateRef<{ name: string; value: string }>>();
  buttonIcon = input<SvgLibraryIcon>();

  selectedOption: WritableSignal<SelectOption | undefined> = signal(undefined);
  searchForm: UntypedFormGroup;

  readonly filteredOptions = signal<SelectOption[] | undefined>(undefined);
  readonly isDropDownOpen = signal(false);
  readonly activeIndex = signal(0);

  /**
   * `options` is declared as a required array input, but callers can still bind it to `null` (e.g. an
   * async pipe that has not emitted yet). Normalize to an array so option lookups (`find`/`indexOf`/
   * `filter`) never crash during change detection.
   */
  private readonly safeOptions = computed(() => this.options() ?? []);

  readonly visibleOptions = computed(() => this.filteredOptions() ?? this.safeOptions());

  readonly dropDown = viewChild<ElementRef>('dropDown');
  readonly selectButton = viewChild<ElementRef>('selectButton');
  readonly buttonContainer = viewChild<ElementRef>('buttonContainer');
  readonly searchInput = viewChild<TextInputComponent>('searchInput');
  readonly optionsList = viewChild<ElementRef>('optionsList');

  readonly dropDownOpen = output<boolean>();
  readonly inputEnterClickGtm = output<void>();

  @HostListener('document:click', ['$event'])
  clickout(event: Event): void {
    if (
      this.isDropDownOpen() &&
      !this.dropDown()?.nativeElement.contains(event.target) &&
      !event.composedPath().includes(this.buttonContainer()?.nativeElement)
    ) {
      this.closeDropDown();
    }
  }

  constructor() {
    this.searchForm = this.fb.group({
      searchInput: this.fb.control(''),
    });

    effect(() => {
      const options = this.safeOptions();
      const ctrlField = this.ctrlField();

      if (!ctrlField) {
        return;
      }

      const matchingOption = options.find((option) => option.value === ctrlField.value);

      if (ctrlField.value && !matchingOption) {
        ctrlField.patchValue('');
      } else if (matchingOption) {
        /**
         * Sync selectedOption when options arrive/change after the value was already set. This
         * handles the race condition where the parent sets the form value before the async-pipe
         * has delivered the new options to this component.
         */
        this.selectedOption.set(matchingOption);
        this.activeIndex.set(options.indexOf(matchingOption));
      }
    });

    effect(() => {
      const activeIndex = this.activeIndex();
      this.optionsList()?.nativeElement.children[activeIndex]?.scrollIntoView?.({
        block: 'nearest',
        behavior: 'smooth',
      });
    });
  }

  ngOnInit(): void {
    const ctrlField = this.ctrlField();
    if (!ctrlField) {
      return;
    }

    ctrlField.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (!value) {
        this.selectedOption.set(undefined);
        this.activeIndex.set(0);
      } else {
        this.selectOptionByValue(this.ctrlField().value);
      }
    });

    if (ctrlField.value) {
      this.selectOptionByValue(ctrlField.value);
    }

    this.searchForm.controls['searchInput'].valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (value && !this.isDropDownOpen()) {
          this.openDropDown(false);
        }
      });
  }

  selectOptionByValue(value: string): void {
    this.selectedOption.set(this.safeOptions().find((option) => option.value === value));

    if (!this.selectedOption()) {
      this.activeIndex.set(0);
      return;
    }

    if (this.searchable()) {
      this.searchForm.controls.searchInput.setValue(this.selectedOption().name);
    }

    this.activeIndex.set(this.safeOptions().indexOf(this.selectedOption()));
  }

  selectOption(option: SelectOption): void {
    this.ctrlField().setValue(option.value);
    this.closeDropDown();
  }

  selectCurrentActiveOption(): void {
    this.selectOption(this.visibleOptions()[this.activeIndex()]);
  }

  closeDropDownOnFocusout(e: FocusEvent): void {
    if (this.isDropDownOpen() && !this.dropDown()?.nativeElement.contains(e.relatedTarget)) {
      this.closeDropDown();
    }
  }

  closeDropDown(): void {
    this.filteredOptions.set(undefined);
    this.searchForm.controls['searchInput'].setValue(this.selectedOption()?.name || '');
    this.isDropDownOpen.set(false);
    this.dropDownOpen.emit(false);
  }

  openDropDown(clearInput = true): void {
    this.isDropDownOpen.set(true);
    this.filteredOptions.set(undefined);

    if (clearInput) {
      this.searchForm.controls.searchInput.setValue('');
    }

    this.dropDownOpen.emit(true);
  }

  onInputEnter(): void {
    this.inputEnterClickGtm.emit();

    if (this.isDropDownOpen()) {
      this.selectCurrentActiveOption();
    } else {
      this.openDropDown();
    }
  }

  handleKeyDown(e: KeyboardEvent): void {
    const currentActiveIndex = this.activeIndex();
    const maxIndex = Math.max(0, this.visibleOptions().length - 1);

    switch (e.key) {
      case 'Escape':
        this.closeDropDown();
        break;
      case 'Down':
      case 'ArrowDown':
        stopPropagation(e);
        if (this.isDropDownOpen()) {
          this.activeIndex.set(currentActiveIndex + 1 > maxIndex ? 0 : currentActiveIndex + 1);
        } else {
          this.openDropDown();
        }
        break;
      case 'Up':
      case 'ArrowUp':
        stopPropagation(e);
        if (this.isDropDownOpen()) {
          this.activeIndex.set(currentActiveIndex - 1 < 0 ? maxIndex : currentActiveIndex - 1);
        } else {
          this.openDropDown();
          this.activeIndex.set(maxIndex);
        }
        break;
    }
  }

  handleSearch(): void {
    const query = (this.searchForm.controls['searchInput'].value ?? '').toLowerCase();

    this.filteredOptions.set(this.safeOptions().filter((option) => option.name?.toLowerCase().startsWith(query)));
  }

  clearSearch(e: Event): void {
    this.searchForm.controls.searchInput.setValue('');
    this.closeDropDown();
    this.searchInput()?.inputElement()?.nativeElement.focus();
    e.stopPropagation();
  }
}
